// MobX store: Copilot session lifecycle, turn loop, chat history, status.
// Entry point is gated by the copilot_enabled Flagsmith flag — abort on
// construction if flag is off (see index.js which wraps the lazy load).
// Per plan Task 11.4 and spec § 6.1.

import { makeObservable, observable, action, computed, runInAction } from "mobx";
import { getScale, MAX_LONG_EDGE } from "./coordinateScaling";
import { executeActions } from "./actionExecutor";
import { ensureCsrf, startSession, postTurn, endSession } from "./apiClient";
import { logEvent } from "../log";
import { errorKind } from "../log/clientEventSchema";
import { extractPlanBody, extractCurrentStep, parseCurrentStep } from "./planParser";
import { captureScreenshot, getGuacClient } from "./guacamoleHook";
import { clearAnnotations, dismissAgentCursor } from "./annotationOverlay";
import { renderRunBrief } from "./workflows/workflowsApi";
import { user_id } from "../userData";
import { RecordingController } from "./workflows/recording/RecordingController";

/**
 * Ensure every action has a stable string id. Kyoto's contract returns id;
 * the fallback `${messageIndex}-${actionIndex}` is defensive insurance so
 * convertMessage and _pendingConfirms always key on the same identifier.
 */
function normalizeActionIds(actions, messageIndex) {
  return actions.map((a, i) => (a.id ? a : { ...a, id: `${messageIndex}-${i}` }));
}

/** Appended to recoverable system messages (rate-limit, turn-level error,
 *  network blip, max-iter cap, cancelled). The model re-reads `current_plan`
 *  + `current_step` from session state on every turn, so typing "continue"
 *  resumes the same task instead of asking the user to rephrase the prompt.
 *  Not appended to terminal states (cap-blocked, session-not-found) where
 *  the session is gone and "continue" would just fail again. */
const CONTINUE_HINT = ' Say "continue" to resume.';

/** localStorage key for the user's compact-mode preference (hide
 *  screenshots and action cards). Persisted across sessions. */
const COMPACT_STORAGE_KEY = "copilot:compact";

function readCompactPref() {
  try {
    return localStorage.getItem(COMPACT_STORAGE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function writeCompactPref(value) {
  try {
    localStorage.setItem(COMPACT_STORAGE_KEY, value ? "1" : "0");
  } catch (_) { /* non-fatal */ }
}

export class CopilotController {
  /** Hard cap on agent-loop iterations per user request. The prototype
   *  used 50; mirror that. The model normally stops well before this. */
  static MAX_ITERATIONS = 50;

  /** @type {string|null} */
  sessionId = null;

  /** @type {RecordingController|null} */
  recordingController = null;

  /** @type {boolean} True while a /turn request is in-flight. */
  busy = false;

  /** @type {boolean} When true, the chat hides per-turn screenshots and
   *  action cards, showing only the model's prose. Survives session/panel
   *  reopens via localStorage. Safety-confirm prompts remain visible
   *  regardless because they require user interaction. */
  compact = false;

  /**
   * Chat history entries: { role: "user"|"assistant"|"system", text: string }
   * @type {Array<object>}
   */
  messages = [];

  /**
   * Map from action id → resolve fn. Populated when an action requires safety
   * confirmation; cleared when the user clicks Allow/Deny. The class-field
   * initializer (`new Map()`) is load-bearing: makeObservable below needs an
   * own-property to attach to, and a bare `_pendingConfirms;` declaration
   * gets dropped by Babel/CRA, producing a real-MobX "Field not found" error.
   * The `observable` annotation in makeObservable auto-promotes the plain
   * Map to an ObservableMap.
   * @type {import("mobx").ObservableMap<string, function>}
   */
  _pendingConfirms = new Map();

  /**
   * AbortController for the in-flight /turn fetch. Recreated at the top of
   * every ask(); cancel() calls .abort() on it to unblock the awaited fetch
   * with an AbortError. Null when no turn is in flight.
   * @type {AbortController|null}
   */
  _abortController = null;

  /**
   * Set by cancel() to flag the agent loop to exit cleanly at the next
   * checkpoint. Distinct from busy: busy stays true until the in-flight
   * await actually unwinds, but _aborted is set immediately on cancel so
   * the loop's next iteration check sees it.
   * @type {boolean}
   */
  _aborted = false;

  /**
   * The body of the latest `<plan>...</plan>` block the model emitted, or "".
   * Mirrors kyoto's `state.current_plan` — see prompts.py § "How to think and
   * act". Updated only when a new plan block is parsed from assistant_text,
   * so transient prose-only turns don't clear the progress strip.
   * @type {string}
   */
  currentPlan = "";

  /**
   * The latest `Current step:` value the model emitted (e.g. "2/5 — opening
   * the file manager"), or "". Mirrors kyoto's `state.current_step`. Same
   * update discipline as currentPlan: only refreshed when a new value is
   * parsed.
   * @type {string}
   */
  currentStep = "";

  /**
   * Wall-clock ms timestamp of the last successful `/turn` response, or null
   * if no turn has completed in this session. Feeds the `isMidTask` getter,
   * which gates the close-connection confirmation dialog. Mirrors kyoto's
   * `last_turn_at` on the server side, but tracked locally so the
   * close-handler doesn't need an HTTP round-trip just to decide whether
   * to prompt.
   * @type {number|null}
   */
  _lastTurnAt = null;

  // Tier 1.c § 4.3 — Guide Mode state.
  controlPermission             = "ungranted"; // "ungranted" | "granted" | "denied"
  controlPermissionRemembered   = false;       // sticky Allow within session
  pendingPermissionCard         = null;        // { taskHint, resolve } | null
  _pendingGuideStepOnLoopEnd    = false;       // transient signal carried iter→iter
  guideStepPending              = false;       // UI-render flag for [Done|Stop] chip
  currentTaskHint               = "";          // truncated user message for card
  // Synthetic tool_results from a short-circuited Guide-only turn. The
  // outgoing /turn after a show_on_screen carries these so kyoto records
  // the action as completed (avoids dangling-action recovery on the next
  // user message). Cleared on session reset.
  _pendingToolResults           = null;

  // Slot tracking (B spec § 7.1a) — driven by chat panel from session options.
  currentSlot  = null;
  defaultSlot  = null;
  allowedSlots = [];

  // Workflow library state (B spec § 8.3).
  workflowsLibraryOpen = false;
  workflowsLoading     = false;
  workflowsError       = null;
  workflows            = [];
  workflowParamPrompt  = null;   // null | { workflow, paramValues }
  currentWorkflow      = null;   // null | { id, name, params, phase: "running"|"completed" } — for the run marker

  /**
   * @param {HTMLIFrameElement} iframe - The active VM iframe (contains Guacamole).
   * @param {number|string} permissionId - cloud_permission row id for the active
   *   connection (Denver's `active_perm`). Kyoto resolves this to a vm_id.
   */
  constructor(iframe, permissionId) {
    makeObservable(this, {
      sessionId: observable,
      busy: observable,
      messages: observable,
      compact: observable,
      _pendingConfirms: observable,
      currentPlan: observable,
      currentStep: observable,
      _lastTurnAt: observable,
      controlPermission: observable,
      controlPermissionRemembered: observable,
      pendingPermissionCard: observable,
      guideStepPending: observable,
      currentTaskHint: observable,
      currentSlot: observable,
      defaultSlot: observable,
      allowedSlots: observable,
      workflowsLibraryOpen: observable,
      workflowsLoading: observable,
      workflowsError: observable,
      workflows: observable,
      workflowParamPrompt: observable,
      currentWorkflow: observable,
      recordingController: observable.ref,
      // _pendingGuideStepOnLoopEnd is internal — not observable.
      isMidTask: computed,
      canRunWorkflow: computed,
      start: action,
      ask: action,
      stop: action,
      cancel: action,
      resolveConfirm: action,
      toggleCompact: action,
      setSlot: action,
      setDefaultSlots: action,
      openWorkflowsLibrary: action,
      closeWorkflowsLibrary: action,
      executeWorkflow: action,
    });
    this.iframe = iframe;
    this.permissionId = permissionId;
    this.compact = readCompactPref();
    // Model-appropriate long-edge cap (§12). Set per session from
    // startSession()'s recommended_max_long_edge; the default keeps any
    // capture before session start sane.
    this.maxLongEdge = MAX_LONG_EDGE;
  }

  setSlot(slot) {
    // Switching profile changes the agent's voice/mode; any guide
    // annotation drawn by the previous slot no longer applies.
    if (slot !== this.currentSlot) clearAnnotations();
    this.currentSlot = slot;
  }

  setDefaultSlots({ defaultSlot, allowedSlots }) {
    this.defaultSlot = defaultSlot;
    this.allowedSlots = allowedSlots || [];
    if (this.currentSlot == null) this.currentSlot = defaultSlot;
  }

  /**
   * Bind the recording controller once Guacamole is initialized AND the
   * Berlin user id is known. Idempotent. Returns true if bound this call.
   *
   * Derives the Guacamole client internally from this.iframe so chat-panel
   * callers don't need a private accessor. Both prerequisites are checked
   * here so the caller can poke this method at safe rendezvous points
   * (mount effect, MobX reaction on user_id) without worrying about order.
   */
  attachRecordingController() {
    if (this.recordingController) return false;
    const uid = user_id.get();
    if (typeof uid !== "number") return false;
    const guac = getGuacClient(this.iframe);
    if (!guac) return false;
    const rc = new RecordingController({
      userId: uid,
      guac,
      copilotController: this,
      intervalMs: 1500,
    });
    rc.loadIntervalFromStorage();
    rc.loadDrafts().catch(() => {});
    runInAction(() => { this.recordingController = rc; });
    return true;
  }

  openWorkflowsLibrary() {
    this.workflowsLibraryOpen = true;
  }

  closeWorkflowsLibrary() {
    this.workflowsLibraryOpen = false;
    this.workflowParamPrompt = null;
  }

  get canRunWorkflow() {
    return !this.busy;
  }

  /**
   * Run a workflow: render the brief on Kyoto, end any existing session,
   * start a fresh one tagged with workflow_id, and post the brief as the
   * first user message with a freshly captured screenshot.
   * B spec § 7.2.
   */
  async executeWorkflow(workflowId, params, { workflowName } = {}) {
    if (this.busy) {
      throw new Error("copilot: executeWorkflow called while a turn is in flight");
    }
    if (this.currentSlot == null) {
      throw new Error("copilot: no profile slot resolved — call setDefaultSlots first");
    }

    await ensureCsrf();
    const brief = await renderRunBrief(workflowId, params);

    if (this.sessionId) {
      try { await endSession(this.sessionId, { reason: "user_stopped" }); } catch (_) {}
      runInAction(() => { this.sessionId = null; });
    }

    const { sessionId, maxLongEdge } = await startSession({
      permissionId: this.permissionId,
      profileSlot:  this.currentSlot,
      workflowId,
    });
    runInAction(() => {
      this.sessionId = sessionId;
      this.maxLongEdge = maxLongEdge;
      this.currentWorkflow = {
        id: workflowId,
        name: workflowName || null,
        params,
        phase: "running",
      };
      this.messages = [];
      this.currentPlan = "";
      this.currentStep = "";
    });

    try {
      const display = this._displayDimensions();
      const scaling = getScale(display, this.maxLongEdge);
      const firstFrame = await captureScreenshot(this.iframe, scaling, this.maxLongEdge);
      await this.ask(brief, { overrideScreenshot: firstFrame });
    } finally {
      // Flip the marker label from "Running" to "Started from" once the
      // first turn settles. Guarded so a dismiss or another workflow
      // start during the await doesn't get overwritten.
      // IMPORTANT: replace the whole object rather than mutating .phase
      // in place. ChatPanel's observer subscribes to currentWorkflow (the
      // reference), not nested fields — an in-place mutation is invisible
      // to it. Assigning a new object changes identity and triggers a
      // re-render.
      runInAction(() => {
        if (this.currentWorkflow && this.currentWorkflow.id === workflowId) {
          this.currentWorkflow = { ...this.currentWorkflow, phase: "completed" };
        }
      });
    }
  }

  /**
   * True while Copilot is actively working — either a `/turn` is in flight,
   * or a turn completed within the last 60 seconds. Gates the close-connection
   * confirmation dialog (specs/2026-05-15-copilot-connection-pinning-design.md
   * § 6): when the user X's a connection and Copilot is mid-task, prompt;
   * otherwise close silently.
   *
   * The 60s heuristic is well above the worst-case ~30s `/turn` (kyoto spec
   * § 6.1) so a Copilot mid-turn always trips this, and much shorter than the
   * 30-minute idle sweep so a long-idle session doesn't nag.
   */
  get isMidTask() {
    if (this.busy) return true;
    if (this._lastTurnAt == null) return false;
    return (Date.now() - this._lastTurnAt) < 60_000;
  }

  /** Flip the compact display preference and persist it. */
  toggleCompact() {
    this.compact = !this.compact;
    writeCompactPref(this.compact);
  }

  /**
   * Bootstrap CSRF and create a session on Kyoto.
   * @param {string} profileSlot - e.g. "balanced"
   */
  async start(profileSlot) {
    await ensureCsrf(); // populate csrftoken cookie — Cross-cutting § A
    const { sessionId, maxLongEdge } = await startSession({ permissionId: this.permissionId, profileSlot });
    runInAction(() => {
      this.sessionId = sessionId;
      this.maxLongEdge = maxLongEdge;
      this.currentPlan = "";
      this.currentStep = "";
    });
  }

  /**
   * Drive the agent loop for one user request. Per spec § 7, kyoto runs a
   * single provider call per /turn and the browser drives the multi-step
   * loop: capture screenshot → /turn → execute returned actions → /turn
   * again with the fresh screenshot, until the model emits no actions
   * (its "done" signal) or we hit the per-request iteration cap.
   *
   * Only the first iteration carries `userMessage`; subsequent iterations
   * send null so the model interprets them as tool_result follow-ups.
   *
   * @param {string} [userMessage]
   */
  async ask(userMessage, { overrideScreenshot = null, hideFromThread = false } = {}) {
    // Busy guard — FIRST statement, before any state mutation.
    if (this.busy) {
      throw new Error("copilot: ask() called while a turn is in flight");
    }
    if (!this.sessionId) throw new Error("copilot: no active session — call start() first");
    runInAction(() => {
      this.busy = true;
      this._aborted = false;
      this._abortController = new AbortController();
    });

    if (userMessage) {
      // Clear any persistent Guide annotation from the previous turn — a
      // new user task supersedes whatever the model was pointing at.
      // (Overlay review item 2.)
      clearAnnotations();
      // The user is interjecting — dismiss the drive cursor (keeping its
      // position, so the next mouse action resumes with a glide).
      dismissAgentCursor();
      runInAction(() => {
        // hideFromThread: the message goes to the model on the /turn wire
        // but does NOT render as a user bubble. Used by continueGuideStep
        // so the canned plumbing prompt doesn't show up as if the user
        // typed it. Continuation calls also skip the per-task state
        // resets below — they're mid-task, not a new task.
        if (!hideFromThread) {
          this.messages.push({ role: "user", text: userMessage });
          // Plan / step state is OWNED BY THE MODEL — we no longer
          // pre-emptively clear it on every user message. The model
          // replaces the strip on its next <plan> emit, and the
          // monotonic-forward gate on currentStep blocks stale
          // backward step moves. Trade-off: a finished plan can
          // linger as a visual wart if the user types a follow-up
          // ("thanks!") with no new task. The fix for stale strips
          // is the Drive-denial reset in the executor-results
          // handler below, not a blanket per-message clear.
          //
          // Tier 1.c § 4.3 — permission DOES reset per new user
          // task; remembered sticky-Allow is preserved across
          // messages.
          this.controlPermission = "ungranted";
          this.guideStepPending = false;
          this.pendingPermissionCard = null;
          this._pendingGuideStepOnLoopEnd = false;
          this.currentTaskHint =
            userMessage.length > 80 ? userMessage.slice(0, 80) + "…" : userMessage;
        }
      });
    }

    // Flush any tool_results stashed by a prior short-circuited Guide turn
    // so kyoto sees the show_on_screen action as completed.
    let nextToolResults = this._pendingToolResults || [];
    this._pendingToolResults = null;
    let turnRequestId = null;
    // Observation-as-result: the model never asks for a screenshot. The first
    // turn observes the starting screen (an override from a workflow / Guide
    // continuation, or a fresh capture below); every later turn observes the
    // frame the executor captured after the prior action batch settled.
    let nextScreenshotBlob = overrideScreenshot || null;
    try {
      for (let iter = 0; iter < CopilotController.MAX_ITERATIONS; iter++) {
        const display = this._displayDimensions();
        const scaling = getScale(display, this.maxLongEdge);
        if (iter === 0 && !nextScreenshotBlob) {
          // No override — capture the starting frame so the model sees where
          // it begins. Best-effort: a failure just sends no image this turn.
          try {
            nextScreenshotBlob = await captureScreenshot(this.iframe, scaling, this.maxLongEdge);
          } catch (_) {
            nextScreenshotBlob = null;
          }
        }
        const screenshotBlob = nextScreenshotBlob;

        // We reserve the slot index where the assistant message WILL land
        // so action IDs can be stable from the moment the response is
        // parsed. The bubble itself is pushed only after /turn returns —
        // the in-flight signal is the ThinkingBubble, not an empty bubble.
        const messageIndex = this.messages.length;

        // Per spec § 6.4: each /turn POST gets a FRESH UUID. The same UUID
        // is reused only inside _postTurnWithRetry's one-shot network retry
        // (server-side idempotency makes the retry a safe no-op).
        // Use crypto.randomUUID() where available (modern browsers, Electron, Node ≥ 15),
        // fall back to a Math.random-based UUID v4 for JSDOM in tests.
        turnRequestId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
            });

        // cancel() during the post-action capture: these results certainly won't be sent.
        if (this._aborted) {
          this._reportDroppedResults(nextToolResults);
          runInAction(() => {
            this.messages.push({ role: "system", text: "Cancelled." });
          });
          return;
        }

        const { status, body, attempt } = await this._postTurnWithRetry({
          sessionId: this.sessionId,
          turnRequestId,
          screenshotBlob,
          dimensions: { width: scaling.scaledWidth, height: scaling.scaledHeight },
          previousScreenshotBlob: null,
          toolResults: nextToolResults.length ? nextToolResults : null,
          userMessage: iter === 0 ? userMessage : null,
          signal: this._abortController.signal,
        });

        // Cap blocked (HTTP 402) — session is permanently ended by the server
        if (status === 402) {
          runInAction(() => {
            this.messages.push({
              role: "system",
              text: `Usage cap reached (${body?.limit_type || "unknown"}). Session ended.`,
            });
            this.sessionId = null;
          });
          this._pendingToolResults = null;
          return;
        }

        // Not found (HTTP 404) — the cached sessionId points at a session
        // that no longer exists (ended out-of-band, reaped, or never owned).
        // Clear it so the next user action can start a fresh session;
        // without this, the UI loops forever against a dead session id.
        if (status === 404) {
          this._reportTurnFailure(turnRequestId, { status, error_kind: "session_not_found", attempt });
          runInAction(() => {
            this.messages.push({
              role: "system",
              text: "Session no longer active — please start a new Copilot session.",
            });
            this.sessionId = null;
          });
          this._pendingToolResults = null;
          return;
        }

        // Rate-limited (HTTP 429) — session still alive, user can retry.
        // Kyoto sends retry_after_s (sliding-window TTL) in the body; surface
        // it so the user knows when the bucket clears rather than guessing.
        if (status === 429) {
          this._reportTurnFailure(turnRequestId, { status, error_kind: "rate_limited", attempt });
          // Kyoto rate-limits before it reads the request, so a first attempt refused here
          // delivered nothing. After a retry the first attempt may have been accepted.
          if (attempt === 1) this._reportDroppedResults(nextToolResults);
          const retryAfter = Number(body?.retry_after_s);
          const suffix = Number.isFinite(retryAfter) && retryAfter > 0
            ? ` Try again in ${retryAfter}s.`
            : " Please try again in a moment.";
          runInAction(() => {
            this.messages.push({
              role: "system",
              text: `Rate limited.${suffix}${CONTINUE_HINT}`,
            });
          });
          return;
        }

        // Server error
        if (!body) {
          this._reportTurnFailure(turnRequestId, { status, error_kind: "no_body", attempt });
          runInAction(() => {
            this.messages.push({
              role: "system",
              text: `Server error (HTTP ${status}).${CONTINUE_HINT}`,
            });
          });
          return;
        }

        // Per-turn error (HTTP 200 with status: "errored") — session still alive
        if (body.status === "errored") {
          // Copilot received the tool results with this turn; only the turn failed.
          this._reportTurnFailure(turnRequestId, { status, error_kind: body.error_type, attempt });
          runInAction(() => {
            this.messages.push({
              role: "system",
              text: `Turn error: ${body.error_type || body.message || "unknown"}.${CONTINUE_HINT}`,
            });
          });
          return;
        }

        const rawActions = body.actions || [];
        const actions = normalizeActionIds(rawActions, messageIndex);
        const assistantText = body.assistant_text || "";
        // Mirror kyoto's update discipline: refresh plan/step only when the
        // model emitted a new value this turn. An empty extract means the
        // model narrated without re-stating the plan — keep the previous
        // strip rather than blanking the UI between steps.
        const nextPlan = extractPlanBody(assistantText);
        const nextStep = extractCurrentStep(assistantText);
        runInAction(() => {
          // Push the assistant message in full now that we have it. While
          // /turn was in flight, the thread showed only the user message
          // plus ThinkingBubble — no empty assistant placeholder.
          this.messages.push({
            role: "assistant",
            text: assistantText,
            actions,
          });
          // Stamp the mid-task heartbeat — `isMidTask` reads this to decide
          // whether closing the underlying connection should prompt the user.
          this._lastTurnAt = Date.now();
          if (nextPlan) this.currentPlan = nextPlan;
          if (nextStep) {
            // Monotonic-forward: only accept a step value whose N is greater
            // than or equal to the current one's N. Prevents the strip from
            // regressing if the model accidentally restates an earlier step
            // mid-task. A plan rewrite (nextPlan truthy) resets the gate so
            // the next "Current step: 1/M" lands correctly.
            const prev = parseCurrentStep(this.currentStep);
            const next = parseCurrentStep(nextStep);
            if (nextPlan || !prev || (next && next.current >= prev.current)) {
              this.currentStep = nextStep;
            }
          }
        });

        // Tier 1.c § 4.3 — Guide-step lifecycle classifier.
        // Per § 4.3: a visible show_on_screen (circle/arrow/rectangle, or
        // bare kind=undefined which defaults to circle in the executor)
        // qualifies; kind="clear" alone does NOT.
        //
        // The Done/Stop chip only makes sense in a Guide-Mode walkthrough
        // — i.e. the user has explicitly clicked "Just guide me" on the
        // permission card (controlPermission === "denied"). Single-shot
        // "show me where X is" asks happen in the default "ungranted"
        // state; the annotation persists on screen and the conversation
        // continues with whatever the user types next. Without this gate,
        // any show_on_screen turn surfaces the chip, and clicking Done
        // sends a "what's next?" continuation that the model interprets
        // as license to redraw — looping forever on single-shot asks.
        const CONTROL_TYPES = [
          "left_click", "right_click", "middle_click", "double_click", "triple_click",
          "left_click_drag", "type", "key", "scroll", "mouse_move",
        ];
        const hasControl = actions.some((a) => CONTROL_TYPES.includes(a.type));
        const hasShowOnScreenVisible = actions.some((a) =>
          a.type === "show_on_screen" && (a.kind === undefined ||
            ["circle", "arrow", "rectangle"].includes(a.kind)));
        const inGuideMode = this.controlPermission === "denied";
        runInAction(() => {
          if (hasControl) {
            this._pendingGuideStepOnLoopEnd = false;
          } else if (hasShowOnScreenVisible && inGuideMode) {
            this._pendingGuideStepOnLoopEnd = true;
          }
          // else: leave unchanged — propagates across iters
        });

        // No actions = model is done with this user request.
        if (actions.length === 0) {
          // Loop is about to end (no actions). Promote the deferred Guide-step
          // pending signal to the UI-visible flag.
          runInAction(() => {
            if (this._pendingGuideStepOnLoopEnd) {
              this.guideStepPending = true;
            }
            this._pendingGuideStepOnLoopEnd = false;
          });
          return;
        }

        const results = await executeActions(
          this.iframe, scaling, actions, this._confirm,
          (taskHintOverride) => this._requestControlPermission(taskHintOverride),
          this._permissionStateRef(),
        );
        nextToolResults = results;
        runInAction(() => {
          // Persist results onto the producing assistant message for UI history.
          this.messages[messageIndex].actionResults = results;
        });

        // Drive→Guide transition: when the user declined control, the model
        // is forced into Guide mode mid-task. Its previous <plan> was
        // Drive-flavoured ("click X, type Y") and no longer applies — wipe
        // the plan strip + step indicator so the model's next emit lands
        // as a clean replan (the monotonic-forward gate on currentStep
        // would otherwise block the step rollback).
        if (results.some((r) => r?.error === "user_declined_control")) {
          runInAction(() => {
            this.currentPlan = "";
            this.currentStep = "";
          });
        }

        // cancel() may have fired during executeActions. Check before the
        // next iteration so we don't fire another postTurn after the user
        // already asked us to stop. The session stays alive — they can
        // type a fresh message next.
        if (this._aborted) {
          this._reportDroppedResults(results);
          runInAction(() => {
            this.messages.push({ role: "system", text: "Cancelled." });
          });
          return;
        }

        // Tier 1.c § 4.4 — Short-circuit Guide-only turns. show_on_screen
        // is fire-and-forget: the overlay IS the communication, and the
        // sentence ("Click the circled icon") usually comes in the same
        // assistant_text as the tool_use per system prompt § P6. So when
        // a turn's only actions are show_on_screen (± screenshot), skip
        // the second /turn that would otherwise just ask the model to
        // narrate. Stash the synthetic results for the next /turn so kyoto
        // doesn't see the action as dangling on the next user message.
        // Mirror hasShowOnScreenVisible: only VISIBLE annotations qualify
        // (kind=clear is bookkeeping — drop-through to normal loop end).
        const onlyGuide =
          hasShowOnScreenVisible &&
          actions.every((a) => a.type === "show_on_screen" || a.type === "screenshot");
        if (onlyGuide) {
          this._pendingToolResults = results;
          runInAction(() => {
            if (!assistantText.trim()) {
              // Model emitted no narration — surface a minimal fallback so
              // the user knows the overlay is meant to be looked at.
              this.messages.push({
                role: "system",
                text: "Look at the highlighted area.",
              });
            }
            // Promote the deferred Guide-step signal only if we're in a
            // Guide-Mode walkthrough (user clicked "Just guide me"). For
            // single-shot "show me X" asks, the annotation persists on
            // screen and we exit silently — same as the prototype.
            if (inGuideMode) {
              this.guideStepPending = true;
            }
            this._pendingGuideStepOnLoopEnd = false;
          });
          return;
        }

        // Observation for the NEXT turn: capture the settled post-batch frame.
        // executeActions already let the screen settle after the last action,
        // so capture directly. Best-effort — a failure degrades to no image.
        try {
          nextScreenshotBlob = await captureScreenshot(this.iframe, scaling, this.maxLongEdge);
        } catch (_) {
          nextScreenshotBlob = null;
        }
      }

      runInAction(() => {
        this.messages.push({
          role: "system",
          text: `Hit max iterations (${CopilotController.MAX_ITERATIONS}).${CONTINUE_HINT}`,
        });
      });
    } catch (err) {
      // Cancellation path: the user clicked the Cancel button while a fetch
      // was in flight. AbortController.abort() rejects the awaited fetch
      // with an AbortError. Suppress the "Network error" path and instead
      // surface the "Cancelled." system message exactly once.
      if (this._aborted || err?.name === "AbortError") {
        runInAction(() => {
          this.messages.push({ role: "system", text: "Cancelled." });
        });
      } else {
        // Only a /turn that failed after its retry is a Copilot failure; anything else here
        // (running the actions, reading the response) failed on this side.
        if (err?.turnAttempt) {
          this._reportTurnFailure(turnRequestId, { error_kind: errorKind(err), attempt: err.turnAttempt });
        } else {
          logEvent("app.error.unhandled", { error_kind: errorKind(err), source: "copilot_loop" });
        }
        // Network/transport failures (fetch rejection, screenshot/action
        // crashes) surface here. Without this catch, the promise rejects
        // up through the assistant-ui onNew adapter and CRA's dev overlay
        // paints the page red, hiding the chat. Treat as a recoverable
        // per-turn error: keep the session alive, let the user retry.
        runInAction(() => {
          this.messages.push({
            role: "system",
            text: `Network error — ${err?.message || "could not reach Copilot service"}.${CONTINUE_HINT}`,
          });
        });
      }
    } finally {
      runInAction(() => {
        this.busy = false;
        this._abortController = null;
      });
    }
  }

  /**
   * Post a turn with one network-failure retry.
   * Uses the same turnRequestId on retry — the server's idempotency table
   * makes the retry a safe no-op if the first attempt reached the server.
   * Per spec § 6.4.
   *
   * @param {object} params - Full params for postTurn (including sessionId).
   * @returns {Promise<{status: number, body: any}>}
   */
  async _postTurnWithRetry(params) {
    const { sessionId, ...turnParams } = params;
    try {
      return { ...(await postTurn(sessionId, turnParams)), attempt: 1 };
    } catch (err) {
      // Cancellation isn't a transport failure — re-throw so ask()'s catch
      // can route it to the "Cancelled." system message instead of doubling
      // the in-flight load by retrying.
      if (err?.name === "AbortError") throw err;
      // The first failure is otherwise invisible: the retry hides it.
      this._reportTurnFailure(params.turnRequestId, { error_kind: errorKind(err), attempt: 1 });
      // One retry with the same turn_request_id.
      try {
        return { ...(await postTurn(sessionId, turnParams)), attempt: 2 };
      } catch (retryErr) {
        if (retryErr && typeof retryErr === "object") retryErr.turnAttempt = 2;
        throw retryErr;
      }
    }
  }

  // Failure events for support: ids, codes and counts only, never the conversation.
  _reportTurnFailure(turnRequestId, fields) {
    logEvent("copilot.turn.failed", { copilot_session: this.sessionId, turn_request_id: turnRequestId, ...fields });
  }

  // Results of actions that ran on the VM and certainly won't reach Copilot. A lost response
  // (no body, a final network failure) is not reported: Kyoto may have stored them.
  _reportDroppedResults(results) {
    if (results && results.length) {
      logEvent("copilot.results.dropped", { copilot_session: this.sessionId, results: results.length });
    }
  }

  /** Cancel the in-flight turn. The composer's Send button swaps to a Cancel
   *  button while busy; clicking it routes here via the runtime's onCancel.
   *
   *  Mechanics:
   *    - Flag _aborted so the loop's iteration-boundary checks exit cleanly.
   *    - Abort the in-flight /turn fetch so the awaited postTurn rejects
   *      immediately (AbortError) instead of waiting out the LLM call.
   *    - Resolve any open safety-confirm dialogs as "denied" so an open
   *      Allow/Deny prompt unblocks executeActions, which then unwinds.
   *    - Leave the session alive — the user can send a new message in
   *      the same conversation immediately. (Use stop() to end the session.)
   *
   *  No-op when no turn is in flight. */
  cancel() {
    if (!this.busy) return;
    this._aborted = true;
    if (this._abortController) {
      this._abortController.abort();
    }
    // Stop button — the user is interrupting; whatever the model was
    // pointing at is no longer guidance they care about.
    clearAnnotations();
    // Iterate a snapshot of the keys — resolveConfirm mutates the underlying
    // ObservableMap, and Maps iterated directly while being mutated can
    // skip entries.
    const ids = Array.from(this._pendingConfirms.keys());
    for (const id of ids) {
      this.resolveConfirm(id, "denied");
    }
    // Tier 1.c — also resolve any pending permission card so executeActions
    // doesn't deadlock awaiting a decision that will never come.
    if (this.pendingPermissionCard) {
      const card = this.pendingPermissionCard;
      runInAction(() => { this.pendingPermissionCard = null; });
      card.resolve("denied");
    }
  }

  /** End the session gracefully. Screenshots are inlined data URLs so there
   *  is no Blob/URL handle to free; the strings drop out of memory when the
   *  controller (and its messages array) is dereferenced by the panel host.
   *
   *  @param {string} [reason] - One of kyoto's client-callable end reasons
   *    (user_stopped, user_navigated_away, vm_disconnected, connection_closed).
   *    Omit to let kyoto default to `user_stopped` — preserves the wire shape
   *    for existing callers that don't care which reason gets recorded. */
  async stop(reason) {
    if (!this.sessionId) return;
    if (reason) {
      await endSession(this.sessionId, { reason });
    } else {
      await endSession(this.sessionId);
    }
    runInAction(() => { this.sessionId = null; });
    this._pendingToolResults = null;
  }

  /**
   * Resolve a pending safety confirmation.
   * @param {string} toolCallId — the action id
   * @param {"allowed"|"denied"} decision
   */
  resolveConfirm(toolCallId, decision) {
    const resolve = this._pendingConfirms.get(toolCallId);
    if (resolve) {
      this._pendingConfirms.delete(toolCallId);
      resolve(decision);
    }
  }

  /**
   * Resume a Guide walkthrough after the user clicked "Done — what's next?"
   * on the inline advance affordance. Captures a fresh screenshot of the
   * post-user-action state and pushes it with a canned continuation message.
   *
   * Per spec § 4.6.
   */
  async continueGuideStep() {
    if (this.busy) {
      throw new Error("copilot: continueGuideStep while busy");
    }
    if (!this.sessionId) {
      throw new Error("copilot: no active session");
    }
    runInAction(() => { this.guideStepPending = false; });
    // User confirmed the step — clear the persistent annotation before
    // asking for the next one. (Overlay review item 2.)
    clearAnnotations();

    const display = this._displayDimensions();
    const scaling = getScale(display, this.maxLongEdge);
    const freshScreenshot = await captureScreenshot(this.iframe, scaling, this.maxLongEdge);
    // Matches the kyoto prototype's post-Done message verbatim. The prior
    // verbose version ("if fully addressed, acknowledge; if more steps,
    // NEXT target; do NOT repeat") read like permission to keep drawing
    // and led to redraws on single-shot asks. The prototype trusts the
    // system prompt's "one step at a time" rule and just asks the
    // question.
    await this.ask(
      "I've completed that step. What should I do next? "
      + "(Continue guiding me — one step at a time.)",
      { overrideScreenshot: freshScreenshot, hideFromThread: true },
    );
  }

  _permissionStateRef() {
    const self = this;
    return {
      get: () => self.controlPermission,
      set: (v) => runInAction(() => { self.controlPermission = v; }),
      get remembered() { return self.controlPermissionRemembered; },
    };
  }

  /**
   * Show the permission card and resolve with "granted" or "denied" when the
   * user clicks Allow or Just-guide-me respectively. Spec § 4.4.
   */
  // Called by executeActions either implicitly (first control action with
  // permission still 'ungranted') or explicitly (the model emitted a
  // request_permission tool call with an override task_hint).
  _requestControlPermission(taskHintOverride) {
    return new Promise((resolve) => {
      runInAction(() => {
        this.pendingPermissionCard = {
          taskHint: taskHintOverride || this.currentTaskHint,
          resolve,
        };
      });
    });
  }

  _confirm = (safetyAction) => {
    // Guard *before* constructing the Promise. A synchronous throw inside
    // `new Promise((resolve) => { throw … })` becomes a rejection, which the
    // outer try/catch in ask() would mislabel as "Network error" to the user.
    // Throwing here surfaces the invariant violation at the executeActions
    // callsite cleanly.
    if (this._pendingConfirms.has(safetyAction.id)) {
      throw new Error(
        `copilot: duplicate safety-confirm for action id "${safetyAction.id}" ` +
        `(type "${safetyAction.type}") — would silently overwrite the pending ` +
        `resolver. Action ids must be unique while their confirms are pending.`
      );
    }
    return new Promise((resolve) => {
      runInAction(() => {
        this._pendingConfirms.set(safetyAction.id, resolve);
      });
    });
  };

  // Read display dimensions from the Guacamole iframe's Angular client.
  // Pulls the live Guacamole.Client off the `.display` element's scope —
  // mirrors resize.js:77 and avoids guacClientManager.getManagedClient(),
  // which is destructive (see guacamoleHook.js getGuacClient).
  //
  // Returns the SOURCE CANVAS dimensions, not display.getWidth/Height.
  // captureScreenshot() reads pixels from the canvas; the scale factor
  // used to map model coords back to native pixels must come from the
  // same frame, or clicks land off by the canvas-vs-display delta
  // (e.g. canvas 1728x1280 vs display 1684x1252 → ~20-40 px error).
  _displayDimensions() {
    try {
      const displayEl = this.iframe.contentDocument.querySelector(".display");
      if (!displayEl) throw new Error("no .display element");
      const scope = this.iframe.contentWindow.angular.element(displayEl).scope();
      const guacDisplay = scope.client.client.getDisplay();
      const canvas = guacDisplay.getDefaultLayer().getCanvas();
      return { width: canvas.width, height: canvas.height };
    } catch (_) {
      // Fallback: use the iframe's rendered size
      return {
        width: this.iframe.clientWidth || 1920,
        height: this.iframe.clientHeight || 1080,
      };
    }
  }
}
